<script setup lang="ts">
/**
 * 图层与元素：分组 / 计数 / 折叠 / 搜索 / 点击选中 / 显隐 / 锁定 / 双击重命名 id。
 * 数据全部来自 core/query/list；锁定态是编辑器私有（data-editor-locked），导出必剥。
 */
import { computed, nextTick, ref } from 'vue';
import { useEditor } from '@/composables/useEditor';
import { isLocked } from '@/core/ops/layer';
import { isCollapsed, toggleCollapsed, ui } from '@/stores/editor';

const editor = useEditor();
const groups = computed(() => editor.filteredGroups.value);
const selected = computed(() => new Set(editor.selection.value));

const renaming = ref<Element | null>(null);
const renameValue = ref('');
let renameBox: HTMLInputElement | null = null;

const onRow = (el: Element, e: MouseEvent): void => {
  if (renaming.value === el) return; // 重命名中不要顺手改选中
  editor.select([el], e.shiftKey ? 'add' : 'replace');
};

const isHidden = (el: Element): boolean => el.getAttribute('display') === 'none';

const toggleEye = (el: Element, e: MouseEvent): void => {
  e.stopPropagation();
  e.preventDefault();
  editor.toggleHidden([el]);
};

const toggleLock = (el: Element, e: MouseEvent): void => {
  e.stopPropagation();
  e.preventDefault();
  editor.lockElement(el, !isLocked(el));
};

const startRename = (el: Element): void => {
  renaming.value = el;
  renameValue.value = el.getAttribute('id') ?? '';
  void nextTick(() => {
    renameBox?.focus();
    renameBox?.select();
  });
};

const commitRename = (): void => {
  const el = renaming.value;
  renaming.value = null;
  if (!el) return;
  const next = renameValue.value.trim();
  if (next === (el.getAttribute('id') ?? '')) return;
  editor.renameElementId(el, next);
};

const cancelRename = (): void => {
  renaming.value = null;
};

const setRenameBox = (el: unknown): void => {
  renameBox = (el as HTMLInputElement | null) ?? null;
};
</script>

<template>
  <aside class="list-panel" aria-label="图层与元素">
    <div class="panel-head">
      <span class="panel-title">图层</span>
      <span class="panel-count">{{ editor.list.value.total }}</span>
      <label class="search">
        <input
          v-model="ui.search"
          type="search"
          placeholder="按类型 / id 过滤"
          spellcheck="false"
          :disabled="editor.blank.value"
        />
      </label>
    </div>

    <div class="list-scroll">
      <div v-for="group in groups" :key="group.key" class="group">
        <button class="group-head" :aria-expanded="!isCollapsed(group.key)" @click="toggleCollapsed(group.key)">
          <svg
            class="chev"
            :class="{ 'is-collapsed': isCollapsed(group.key) }"
            viewBox="0 0 12 12"
            width="10"
            height="10"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          >
            <path d="m3 4.5 3 3 3-3" />
          </svg>
          <span>{{ group.label }}</span>
          <span class="n">{{ group.total }}</span>
        </button>

        <div v-show="!isCollapsed(group.key)">
          <div
            v-for="item in group.items"
            :key="item.key"
            class="row-wrap"
            :class="{ 'is-hidden': isHidden(item.element), 'is-locked': isLocked(item.element) }"
          >
            <input
              v-if="renaming === item.element"
              :ref="setRenameBox"
              class="rename-input"
              :value="renameValue"
              spellcheck="false"
              @input="renameValue = ($event.target as HTMLInputElement).value"
              @keydown.enter.prevent="commitRename"
              @keydown.esc.prevent="cancelRename"
              @blur="commitRename"
            />
            <template v-else>
              <button
                class="row"
                :class="{ 'is-selected': selected.has(item.element) }"
                :title="`${item.tag} ${item.label}（双击改 id）`"
                @click="onRow(item.element, $event)"
                @dblclick="startRename(item.element)"
              >
                <span class="dot" />
                <span class="tag">{{ item.tag }}</span>
                <span class="id">{{ item.label }}</span>
                <span class="sw" :style="{ background: item.swatch ?? 'transparent' }" />
              </button>
              <button
                class="eye"
                :class="{ 'is-off': isHidden(item.element) }"
                :title="isHidden(item.element) ? '显示' : '隐藏'"
                :aria-label="isHidden(item.element) ? '显示' : '隐藏'"
                @click="toggleEye(item.element, $event)"
              >
                <svg viewBox="0 0 12 12" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round">
                  <path d="M1 6s2-3.2 5-3.2S11 6 11 6s-2 3.2-5 3.2S1 6 1 6Z" />
                  <circle cx="6" cy="6" r="1.4" fill="currentColor" stroke="none" />
                  <path v-if="isHidden(item.element)" d="M2.4 9.6 9.6 2.4" />
                </svg>
              </button>
              <button
                class="lock"
                :title="isLocked(item.element) ? '解锁（画布上可点选）' : '锁定（画布上点不动）'"
                :aria-label="isLocked(item.element) ? '解锁' : '锁定'"
                @click="toggleLock(item.element, $event)"
              >
                <svg viewBox="0 0 12 12" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round">
                  <rect x="2.5" y="5.5" width="7" height="5" rx="1" />
                  <path v-if="isLocked(item.element)" d="M4.4 5.5V4a1.6 1.6 0 0 1 3.2 0v1.5" />
                  <path v-else d="M4.4 5.5V4a1.6 1.6 0 0 1 3.2 0" />
                </svg>
              </button>
            </template>
          </div>
        </div>
      </div>

      <p v-if="groups.length === 0" class="empty-hint">
        {{ editor.svg.value ? (editor.list.value.total === 0 ? '画布还是空的' : '没有匹配的元素') : '还没有打开文档' }}
      </p>
    </div>
  </aside>
</template>
